import { useQuery } from '@tanstack/react-query';
import { AdminNav } from '../components/AdminNav';
import { useAdminApi } from '../api/useAdminApi';

// Phase 3.5 — /admin Health panel.
//
// Polls /api/manage/health every 30s and surfaces each subsystem's state
// in a glanceable card. Replaces "wait for an email from Azure Monitor"
// as the primary observability surface for routine checks. Alert emails
// are kept (they fire while you're away from this page), but the panel
// is the place an operator looks first.

interface HealthCheck {
  ok: boolean;
  latencyMs: number;
  error?: string;
}

interface HealthResponse {
  ok: boolean;
  checks: {
    cosmos:   HealthCheck;
    keyVault: HealthCheck;
    graph:    HealthCheck;
    mailbox:  HealthCheck & { probedRoom?: string };
  };
  kiosks: {
    online:  number;
    offline: number;
    unbound: number;
    revoked: number;
  };
  timestamp: string;
}

const CHECK_LABELS: Record<keyof HealthResponse['checks'], { title: string; sub: string }> = {
  cosmos:   { title: 'Cosmos DB',  sub: 'bindings + rooms config' },
  keyVault: { title: 'Key Vault',  sub: 'GRAPH-CLIENT-SECRET reachable' },
  graph:    { title: 'Microsoft Graph', sub: 'token + tenant probe' },
  mailbox:  { title: 'Mailbox access', sub: 'access-policy live for a real room' },
};

function StatusDot({ ok }: { ok: boolean }) {
  return <span className={`health-dot health-dot-${ok ? 'ok' : 'fail'}`} aria-hidden="true" />;
}

function CheckCard({ kind, check }: {
  kind: keyof HealthResponse['checks'];
  check: HealthResponse['checks'][keyof HealthResponse['checks']];
}) {
  const meta = CHECK_LABELS[kind];
  const probed = 'probedRoom' in check ? check.probedRoom : undefined;
  return (
    <div className={`card health-check health-check-${check.ok ? 'ok' : 'fail'}`}>
      <div className="health-check-head">
        <StatusDot ok={check.ok} />
        <div>
          <h3>{meta.title}</h3>
          <p className="muted">{meta.sub}{probed ? ` · probed ${probed}` : ''}</p>
        </div>
        <span className="health-check-latency">{check.latencyMs}ms</span>
      </div>
      {check.error ? <p className="health-check-error"><code>{check.error}</code></p> : null}
    </div>
  );
}

export function AdminHealth() {
  const { apiFetch, signedIn } = useAdminApi();

  const healthQ = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: () => apiFetch<HealthResponse>('/api/manage/health'),
    enabled: signedIn,
    refetchInterval: 30_000,
    staleTime: 0,
  });

  return (
    <>
      <AdminNav />

      <section className="card">
        <p className="eyebrow">Operate</p>
        <h2>
          Health
          {healthQ.data ? (
            <span className={`health-pill health-pill-${healthQ.data.ok ? 'ok' : 'fail'}`}>
              {healthQ.data.ok ? 'All systems go' : 'Degraded'}
            </span>
          ) : null}
        </h2>
        <p className="muted">
          Live status of WIC-Conf's dependencies. Refreshes every 30s.
          {healthQ.data ? ` Last update: ${new Date(healthQ.data.timestamp).toLocaleTimeString()}.` : ''}
          {' '}For deeper triage, see <code>docs/app-insights-queries.md</code>.
        </p>
      </section>

      {healthQ.error ? (
        <section className="card error">
          <h3>Health endpoint unreachable</h3>
          <p><code>{(healthQ.error as Error).message}</code></p>
          <p className="muted">
            The endpoint itself is down (or you're signed out). If the alert emails
            haven't fired either, check the Function App at the Azure Portal directly.
          </p>
        </section>
      ) : null}

      {healthQ.data ? (
        <>
          <section className="health-grid">
            <CheckCard kind="cosmos"   check={healthQ.data.checks.cosmos} />
            <CheckCard kind="keyVault" check={healthQ.data.checks.keyVault} />
            <CheckCard kind="graph"    check={healthQ.data.checks.graph} />
            <CheckCard kind="mailbox"  check={healthQ.data.checks.mailbox} />
          </section>

          <section className="card">
            <h3>Kiosk fleet</h3>
            <div className="health-fleet">
              <div className="health-fleet-cell">
                <div className="health-fleet-num">{healthQ.data.kiosks.online}</div>
                <div className="health-fleet-label">Online</div>
              </div>
              <div className="health-fleet-cell">
                <div className="health-fleet-num health-fleet-warn">{healthQ.data.kiosks.offline}</div>
                <div className="health-fleet-label">Offline (bound, &gt;2 min since check-in)</div>
              </div>
              <div className="health-fleet-cell">
                <div className="health-fleet-num">{healthQ.data.kiosks.unbound}</div>
                <div className="health-fleet-label">Awaiting room assignment</div>
              </div>
              <div className="health-fleet-cell">
                <div className="health-fleet-num">{healthQ.data.kiosks.revoked}</div>
                <div className="health-fleet-label">Revoked</div>
              </div>
            </div>
          </section>
        </>
      ) : !healthQ.error ? (
        <section className="card">
          <p className="muted">Probing dependencies…</p>
        </section>
      ) : null}
    </>
  );
}
